/**
 * @file Общие HTTP-запросы и адресация туннелей.
 *
 * makeRequest задаёт тайм-аут и политику повторов; makeTunnelRequest добавляет
 * адрес хранилища и API-токен. Повтор изменяющей операции допустим только
 * там, где вызывающий код знает, что это безопасно.
 */
import { abortableDelay } from './abort.ts'

const DEFAULT_RETRY_COUNT = 0
// Этот тайм-аут делаем побольше, чтобы избежать проблем с низкой скоростью интернета.
const DEFAULT_REQUEST_TIMEOUT_MS = 60_000
const DEFAULT_SLEEP_TIMEOUT_MS = 500

const DEFAULT_RETRY_ATTEMPTS = 5

const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504])

type MakeRequestParams = {
    path: string
    opts?: RequestInit | null
    retryCount?: number
    timeout?: number
    pause?: number
}

type MakeTunnelRequestParams = MakeRequestParams & {
    boxID: string
    token: string
}

export async function makeRequest({
    path,
    opts = null,
    retryCount = DEFAULT_RETRY_COUNT,
    timeout = DEFAULT_REQUEST_TIMEOUT_MS,
    pause = DEFAULT_SLEEP_TIMEOUT_MS,
}: MakeRequestParams): Promise<Response> {
    const callerSignal = opts?.signal ?? new AbortController().signal

    // retryCount — число дополнительных попыток после первой.
    for (let attempt = 0; ; attempt++) {
        callerSignal.throwIfAborted()

        try {
            const response = await fetch(path, {
                ...opts,
                signal: AbortSignal.any([
                    callerSignal,
                    AbortSignal.timeout(timeout),
                ]),
            })
            callerSignal.throwIfAborted()

            // Любой окончательный HTTP-ответ возвращается вызывающему коду.
            // Количество повторов не меняет способ обработки 402, 403 и других статусов.
            if (
                response.ok ||
                !RETRYABLE_STATUSES.has(response.status) ||
                attempt >= retryCount
            )
                return response

            // Тело промежуточного ответа больше не нужно; освобождаем соединение.
            void response.body?.cancel().catch(() => {})
        } catch (error) {
            callerSignal.throwIfAborted()
            if (attempt >= retryCount) throw error
        }

        // Отмена прерывает и запрос, и паузу перед следующей попыткой.
        await abortableDelay(pause, callerSignal)
    }
}

/**
 * Выполняет запрос с некоторым дефолтным кол-вом повторных попыток.
 *
 * Использовать лучше с запросами, которые действительно можно выполнять повторно:
 * например, любое чтение или удаление некоторых данных, если бекенд это позволяет.
 */
export function makeRequestWithRetry({
    path,
    opts,
}: {
    path: string
    opts?: RequestInit | null
}): Promise<Response> {
    return makeRequest({
        path,
        opts,
        retryCount: DEFAULT_RETRY_ATTEMPTS,
    })
}

let tunnelBaseDomains: string[] = []
const randomTunnelDomain = () =>
    tunnelBaseDomains[Math.floor(Math.random() * tunnelBaseDomains.length)]

/**
 * Возвращает базовый домен, используемый для формирования URL туннелей.
 *
 * После первого запроса результат кешируется в модуле. Перед возвратом
 * домен нормализуется: удаляются пробелы по краям и завершающие символы `/`.
 */
async function getTunnelBaseDomain(signal?: AbortSignal): Promise<string> {
    if (tunnelBaseDomains.length > 0) {
        return randomTunnelDomain()
    }

    const res = await makeRequestWithRetry({
        path: '/user-api/tunnel-discovery/router/tunnel-servers/http-external',
        opts: { signal },
    })
    if (!res.ok) {
        throw new Error(`failed to fetch tunnel domain. status: ${res.status}`)
    }

    tunnelBaseDomains = (await res.text())
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '')
        .map((v) => {
            while (v.endsWith('/')) {
                v = v.slice(0, -1)
            }

            return v
        })

    if (tunnelBaseDomains.length === 0)
        throw new Error('No tunnel domains available')

    return randomTunnelDomain()
}

export async function makeTunnelRequest({
    path,
    token,
    boxID,
    opts = null,
    retryCount = DEFAULT_RETRY_COUNT,
    timeout = DEFAULT_REQUEST_TIMEOUT_MS,
    pause = DEFAULT_SLEEP_TIMEOUT_MS,
}: MakeTunnelRequestParams): Promise<Response> {
    opts?.signal?.throwIfAborted()
    const headers = new Headers(opts?.headers)
    headers.set('X-Box-Api-Token', token)
    const optsCopy: RequestInit = { ...opts, headers }

    return await makeRequest({
        path:
            (await getTunnelBaseDomain(opts?.signal ?? undefined)) +
            `/tunnel-router/${boxID}` +
            path,
        opts: optsCopy,
        retryCount,
        timeout,
        pause,
    })
}
